import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import ArticleEmbedHost from '../components/articleEmbeds/ArticleEmbedHost';

/**
 * ContentFetcher — fetches an article body from S3, sanitises it, and renders it.
 *
 * ⭐ Bodies may contain EMBED TOKENS — `[[aai-embed:architecture-explorer]]` — which
 * ArticleEmbedHost swaps for live React components. See components/articleEmbeds/registry.js.
 *
 * ⚠️ The token is plain TEXT on purpose. This content passes through two sanitisers (Froala on
 * save, DOMPurify here), and either is free to strip markup it does not recognise. Text survives
 * both, and an author can type it in the WYSIWYG without opening code view.
 *
 * ⛔ `length` truncation and embeds do not mix. A truncated body is a card preview, and cutting
 * mid-token would either render a broken embed or leave the raw token visible in a teaser. When
 * truncating, tokens are stripped rather than rendered.
 */
export const ContentFetcher = ({ url, length }) => {
  const [content, setContent] = useState('Loading...');

  useEffect(() => {
    fetch(url)
      .then(response => response.text())
      .then(data => {
        // sanitize the HTML
        const cleanData = DOMPurify.sanitize(data);
        let showLength = length ? length : cleanData.length;

        const truncatedData = cleanData.length > showLength ? cleanData.substring(0, showLength) + '...' : cleanData;

        setContent(truncatedData);
      })
      .catch((error) => {
        console.error('Error:', error);
        setContent('Error loading content');
      });
  }, [url, length]);

  // A truncated body is a preview card: strip tokens so a teaser never shows raw
  // `[[aai-embed:…]]` text, and never mounts a full explorer inside a summary tile.
  if (length) {
    return (
      <div dangerouslySetInnerHTML={{ __html: stripEmbedTokens(content) }} />
    );
  }

  return <ArticleEmbedHost html={content} />;
};

/** Remove any embed token, leaving the surrounding prose intact. */
function stripEmbedTokens(html) {
  if (!html) return html;
  return html.replace(/\[\[\s*aai-embed\s*:[^\]]*\]\]/gi, '');
}
